import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout/Layout";
import BreadCrumb from "../../components/BreadCrumb/Breadcrumb";
import Button from "../../components/Button/Button";
import InputGroup from "../../components/FormComponent/InputGroup";
import SelectGroup from "../../components/FormComponent/SelectGroup";
import TextAreaGroup from "../../components/FormComponent/TextAreaGroup";
import { Icon } from "@iconify/react";
import { toast, ToastContainer } from "react-toastify";
import { httprequest } from "../../data/api";
import styles from "./questionpool.module.css";

const QUESTIONS_ENDPOINT = "/api/v1/admin/questions";
const OPTION_KEYS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

const generateSlug = (text) => (text || "").toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");

const CreateQuestion = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    subject: "",
    exam_type: "WAEC",
    year: "",
    category: "",
    topic_category: "",
    question_text: "",
    section_type: "objective",
    status: "draft",
    difficulty: "medium",
    region: "Africa",
    country: "Nigeria"
  });

  const [options, setOptions] = useState([
    { key: "A", text: "", isCorrect: false },
    { key: "B", text: "", isCorrect: false }
  ]);

  const [correctAnswer, setCorrectAnswer] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleOptionChange = (idx, value) => {
    const updated = [...options];
    updated[idx].text = value;
    setOptions(updated);
  };

  const handleAddOption = () => {
    const nextKey = OPTION_KEYS[options.length] || `OPT${options.length + 1}`;
    setOptions([...options, { key: nextKey, text: "", isCorrect: false }]);
  };

  const handleSubmit = () => {
    // Validation
    if (!formData.subject.trim()) {
      toast.error("Subject is required");

      return;
    }
    if (!formData.year) {
      toast.error("Year is required");

      return;
    }
    if (!formData.question_text.trim()) {
      toast.error("Question text is required");

      return;
    }
    if (!correctAnswer) {
      toast.error("Please select the correct answer");

      return;
    }

    const optionsObj = {};
    options.forEach((o) => {
      optionsObj[o.key] = o.text;
    });

    const body = {
      exam_type: formData.exam_type,
      subject: formData.subject,
      subject_slug: generateSlug(formData.subject),
      year: Number(formData.year),
      category: formData.category || "Post Secondary",
      difficulty: formData.difficulty,
      region: formData.region,
      country: formData.country,
      section_type: formData.section_type,
      status: formData.status,
      topic_category: formData.topic_category,
      topic_category_slug: generateSlug(formData.topic_category),
      topic_tags: formData.topic_category ? [formData.topic_category.toLowerCase()] : [],
      topic_tags_normalized: formData.topic_category ? [formData.topic_category.toLowerCase()] : [],
      question: {
        text: formData.question_text,
        options: optionsObj,
        correct_answer: correctAnswer,
        subparts: null
      }
    };

    setIsSubmitting(true);
    httprequest
      .post(QUESTIONS_ENDPOINT, body)
      .then(() => {
        toast.success("Question created successfully");
        setTimeout(() => navigate("/question-pool"), 1200);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Failed to create question");
      })
      .finally(() => setIsSubmitting(false));
  };

  return (
    <Layout>
      <div className={styles.createContainer}>
        <BreadCrumb
          link_one="/question-pool"
          link_one_name="Question Pool"
          active="Create Question"
          description="You can create more than one question at once"
        />

        {/* Subject (full width) */}
        <div className={styles.formFull}>
          <InputGroup
            label="Subject"
            inputType="text"
            value={formData.subject}
            onChange={handleChange("subject")}
            required
          />
        </div>

        {/* Exam Type + Year */}
        <div className={styles.formRow}>
          <SelectGroup label="Exam Type">
            <select value={formData.exam_type} onChange={handleChange("exam_type")}>
              <option value="WAEC">WAEC</option>
              <option value="NECO">NECO</option>
              <option value="JAMB">JAMB</option>
              <option value="GCE">GCE</option>
            </select>
          </SelectGroup>
          <InputGroup
            label="Year"
            inputType="number"
            value={formData.year}
            onChange={handleChange("year")}
            required
          />
        </div>

        {/* Subject + Topic */}
        <div className={styles.formRow}>
          <InputGroup
            label="Subject"
            inputType="text"
            value={formData.category}
            onChange={handleChange("category")}
          />
          <InputGroup
            label="Topic"
            inputType="text"
            value={formData.topic_category}
            onChange={handleChange("topic_category")}
          />
        </div>

        {/* Question */}
        <div className={styles.formFull}>
          <TextAreaGroup
            label="Question"
            value={formData.question_text}
            onChange={handleChange("question_text")}
            placeHolder="Type your question here..."
          />
        </div>

        {/* Answer Type + Status */}
        <div className={styles.formRow}>
          <SelectGroup label="Add Answer Type *">
            <select value={formData.section_type} onChange={handleChange("section_type")}>
              <option value="objective">Multi choice</option>
              <option value="theory">Theory</option>
            </select>
          </SelectGroup>
          <SelectGroup label="Status">
            <select value={formData.status} onChange={handleChange("status")}>
              <option value="draft">Draft</option>
              <option value="published">Publish</option>
            </select>
          </SelectGroup>
        </div>

        {/* Answers */}
        <p className={styles.answersTitle}>Answers</p>
        {options.map((opt, idx) => (
          <div className={styles.optionRow} key={opt.key}>
            <div className={styles.optionInputWrapper}>
              <p style={{ fontSize: 13, fontWeight: 500, color: "#101928", margin: "0 0 4px" }}>Option {opt.key}</p>
              <input
                className={styles.optionInput}
                value={opt.text}
                onChange={(e) => handleOptionChange(idx, e.target.value)}
                placeholder={`Enter option ${opt.key}`}
              />
              <button className={styles.imgIcon} type="button" title="Add image">
                <Icon icon="fluent:image-16-regular" />
              </button>
            </div>
            <label className={styles.radioLabel}>
              <input
                type="radio"
                name="correctAnswer"
                checked={correctAnswer === opt.key}
                onChange={() => setCorrectAnswer(opt.key)}
              />
              Correct answer
            </label>
          </div>
        ))}

        <button className={styles.addOptionBtn} type="button" onClick={handleAddOption}>
          <Icon icon="fluent:add-circle-16-regular" /> Add Option
        </button>

        {/* Submit */}
        <div style={{ marginTop: 28 }}>
          <Button
            variant="primary"
            buttonSize="full"
            color="btndefault"
            buttonName={isSubmitting ? "Creating..." : "Create Question"}
            onClick={handleSubmit}
            disabled={isSubmitting}
            isLoading={isSubmitting}
          />
        </div>
      </div>
      <ToastContainer />
    </Layout>
  );
};

export default CreateQuestion;
